import { useMemo } from 'react';
import { Eye } from 'lucide-react';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { Button } from '@ui/button';
import { Badge } from '@ui/badge';
import { Progress } from '@ui/progress';
import { Skeleton } from '@ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ui/table';
import type { Punchlist } from '../types';
import {
  computeCompletionPercent,
  derivePunchlistStatus,
  getItemCountsForPunchlist,
} from '../utils/punch-list-utils';
import { useAreaTagsByProject } from '@/features/area-tags/hooks/useAreaTags';

interface PunchListsTableProps {
  punchlists: Punchlist[];
  isLoading: boolean;
  onViewDetail: (id: string) => void;
}

const STATUS_BADGE: Record<string, { variant: 'default' | 'secondary' | 'outline'; className: string; label: string }> = {
  completed: { variant: 'default', className: 'bg-green-600 hover:bg-green-600', label: 'Completed' },
  in_progress: { variant: 'default', className: 'bg-blue-600 hover:bg-blue-600', label: 'In Progress' },
  empty: { variant: 'secondary', className: '', label: 'Empty' },
};

const PunchListsTable = ({ punchlists, isLoading, onViewDetail }: PunchListsTableProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: areaTags = [] } = useAreaTagsByProject(projectId ?? '', { enabled: !!projectId });
  const areaNameById = useMemo(
    () =>
      areaTags.reduce<Record<number, string>>((acc, areaTag) => {
        acc[areaTag.id] = areaTag.name;
        return acc;
      }, {}),
    [areaTags]
  );

  if (!isLoading && punchlists.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
        No punch lists found
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-10 bg-white text-xs font-medium w-[60px]">View</TableHead>
              <TableHead className="text-xs font-medium">Title</TableHead>
              <TableHead className="text-xs font-medium">Description</TableHead>
              <TableHead className="text-xs font-medium">Status</TableHead>
              <TableHead className="text-xs font-medium w-[140px]">Completion %</TableHead>
              <TableHead className="text-xs font-medium">Items</TableHead>
              <TableHead className="text-xs font-medium">Sections</TableHead>
              <TableHead className="text-xs font-medium">Area</TableHead>
              <TableHead className="text-xs font-medium">Type</TableHead>
              <TableHead className="text-xs font-medium">Created</TableHead>
              <TableHead className="text-xs font-medium">Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 11 }).map((_, j) => (
                      <TableCell key={j} className="py-2">
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : punchlists.map((pl) => (
                  <PunchListRow
                    key={pl.id}
                    punchlist={pl}
                    onView={onViewDetail}
                    areaNameById={areaNameById}
                  />
                ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

function PunchListRow({
  punchlist,
  onView,
  areaNameById,
}: {
  punchlist: Punchlist;
  onView: (id: string) => void;
  areaNameById: Record<number, string>;
}) {
  const status = derivePunchlistStatus(punchlist);
  const completion = computeCompletionPercent(punchlist);
  const { total, completed } = getItemCountsForPunchlist(punchlist);
  const badge = STATUS_BADGE[status];
  const areaLabel =
    punchlist.area == null ? 'General' : areaNameById[punchlist.area] || `Area #${punchlist.area}`;

  return (
    <TableRow>
      <TableCell className="sticky left-0 z-10 bg-white py-1.5">
        <Button variant="ghost" size="sm" onClick={() => onView(punchlist.id)} className="h-7 w-7 p-0">
          <Eye className="h-4 w-4" />
        </Button>
      </TableCell>
      <TableCell className="text-xs py-1.5 max-w-[200px] truncate font-medium">{punchlist.title}</TableCell>
      <TableCell className="text-xs py-1.5 max-w-[200px] truncate text-muted-foreground">
        {punchlist.description || '—'}
      </TableCell>
      <TableCell className="py-1.5">
        <Badge variant={badge.variant} className={`text-xs ${badge.className}`}>
          {badge.label}
        </Badge>
      </TableCell>
      <TableCell className="py-1.5">
        <div className="flex items-center gap-2">
          <Progress value={completion} className="h-2 w-[80px]" />
          <span className="text-xs text-muted-foreground">{completion}%</span>
        </div>
      </TableCell>
      <TableCell className="text-xs py-1.5">
        {completed}/{total}
      </TableCell>
      <TableCell className="text-xs py-1.5">{punchlist.sections.length}</TableCell>
      <TableCell className="text-xs py-1.5">{areaLabel}</TableCell>
      <TableCell className="py-1.5">
        {punchlist.is_template ? (
          <Badge variant="outline" className="text-xs">Template</Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">Instance</Badge>
        )}
      </TableCell>
      <TableCell className="text-xs py-1.5 whitespace-nowrap">
        {format(new Date(punchlist.created_at), 'MMM d, yyyy')}
      </TableCell>
      <TableCell className="text-xs py-1.5 whitespace-nowrap">
        {format(new Date(punchlist.updated_at), 'MMM d, yyyy')}
      </TableCell>
    </TableRow>
  );
}

export default PunchListsTable;
